import { Loading } from "@edgelogistics/edge-ui/lib/components";
import { OfferT } from "../../../../domain";
import { StatusFormat } from "../status-format";
import { useOfferNegociations } from "../../../services";
import { Button, WarningIcon } from "../../../../../../shared/infrastructure";
import {
  CurrencyFormat,
  DateFormat,
} from "../../../../../../shared/infrastructure/ui/components/libs/table/formats";
import React, { useEffect } from "react";

type SeeNegociationsProps = {
  offer: OfferT;
  close: () => void;
};

export const SeeNegociations = ({
  offer,
  close,
}: SeeNegociationsProps): React.ReactElement => {
  const { data, isLoading, refetch, isRefetching } = useOfferNegociations(
    offer.approval_id
  );

  const columns = [
    "Posted Date",
    "Posted By",
    "Status",
    "Approver",
    "Change Date",
    "Carrier",
    "Rate",
  ];

  useEffect(() => {
    void refetch();
  }, [offer.approval_id, refetch]);

  return (
    <section>
      <h1 className="mb-5 text-[20px] font-[700]">Negotiation History</h1>
      <p className="text-sm font-bold not-italic leading-[normal]">
        Order No. {offer.order_number}
      </p>
      <div className="negociations-columns mt-5 mb-5 grid grid-cols-[repeat(7,1fr)] gap-[30px]">
        {columns.map((column) => (
          <div
            key={column}
            className="negociations-column text-[11px] font-[700] leading-[normal,]"
          >
            {column}
          </div>
        ))}
      </div>

      <div className="offer-row mb-[30px] mt-2.5 grid grid-cols-[repeat(7,1fr)] gap-[30px]">
        <div className="text-[12px] font-normal not-italic leading-[normal] text-[#888889]">
          <DateFormat format="MM/DD/YYYY, hh:mm" data={offer.created_at} />
        </div>
        <div className="text-[12px] font-normal not-italic leading-[normal] text-black">
          {offer?.posted_by_user?.name ?? "- -"}
        </div>
        <div className="text-[12px] font-normal not-italic leading-[normal]">
          <StatusFormat withoutDot data={offer} />
        </div>
        <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[12px] font-normal not-italic leading-[normal] text-black">
          {offer?.approver_user?.name ?? "- -"}
        </div>
        <div className="text-[12px] font-normal not-italic leading-[normal] text-[#888889]">
          {offer?.status_change_date ? (
            <DateFormat
              data={offer?.status_change_date}
              format="MM/DD/YYYY, hh:mm"
            />
          ) : (
            "- -"
          )}
        </div>
        <div className="text-[12px] font-normal not-italic leading-[normal] text-black">
          <span className="grid grid-flow-col items-center justify-start gap-[8px] capitalize text-[#000000]">
            {offer?.carrier?.status === "I" && <WarningIcon fill="#F44336" />}
            <span className="overflow-hidden text-ellipsis whitespace-nowrap">
              {offer?.carrier?.name?.toLowerCase() ?? "- -"}
            </span>
          </span>
        </div>
        <div className="text-[12px] font-normal not-italic leading-[normal] text-black">
          <span className="grid grid-flow-col items-center gap-[8px] whitespace-nowrap text-[#000000]">
            <CurrencyFormat data={offer.rate} />
          </span>
        </div>
      </div>

      {isLoading || isRefetching ? <Loading /> : null}

      {!isLoading &&
        !isRefetching &&
        data?.data.map((negociation, index) => (
          <div key={negociation.id} className="relative">
            <div
              style={{
                gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 1fr",
              }}
              className={`offer-row relative mt-2.5 grid gap-[30px] ${
                index % 2 === 0 ? "bg-[#EAEAEA]" : ""
              }`}
            >
              <div className="relative bg-white">
                <div
                  className="absolute left-0 top-0 h-[150%] w-[31px] translate-y-[-70%] border-l-[0.5px] border-b-[0.5px] border-dashed border-l-[rgb(20,126,251)] border-r-[none] border-t-[none] border-b-[rgb(20,126,251)]"
                  style={{
                    borderImage: "initial",
                  }}
                />
                <div
                  style={{
                    justifyContent: "end",
                  }}
                  className={`ml-[40px] flex content-center ${
                    index % 2 === 0 ? "bg-[#EAEAEA]" : ""
                  } items-center pr-[10px] text-[12px] font-normal not-italic leading-[normal] text-[#888889]`}
                >
                  {data.data.length - index}
                </div>
              </div>
              <div className="text-[12px] font-normal not-italic leading-[normal] text-[#888889]"></div>
              <div className="text-[12px] font-normal capitalize not-italic leading-[normal]">
                {negociation.status}
              </div>
              <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[12px] font-normal not-italic leading-[normal] text-black">
                {negociation.approver_user?.name ?? "- -"}
              </div>
              <div className="text-[12px] font-normal not-italic leading-[normal] text-black">
                <DateFormat
                  data={negociation.createdAt.toString()}
                  format="MM/DD/YYYY, hh:mm"
                />
              </div>
              <div className="text-[12px] font-normal not-italic leading-[normal] text-black"></div>
              <div className="text-[12px] font-normal not-italic leading-[normal] text-black">
                <span className="grid grid-flow-col items-center gap-[8px] whitespace-nowrap text-[#000000]">
                  <CurrencyFormat data={negociation.rate} />
                </span>
              </div>
            </div>
          </div>
        ))}

      <div className="mt-[30px] flex w-[100%] justify-center">
        <Button
          className="w-auto px-[16px] py-[6px] text-[12px]"
          onClick={close}
        >
          Accept
        </Button>
      </div>
    </section>
  );
};
